import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import ApiInspector from '../../components/ApiInspector';
import CondizioniHtmlEditor from '../../components/CondizioniHtmlEditor';
import { apiFetch } from '../../lib/api';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { API_DOCS } from './CondizioniDocumentoPage.api';
import './CondizioniDocumentoPage.css';

const LABEL_TIPO: Record<string, string> = {
  COMMESSA: 'Commessa',
  PREVENTIVO: 'Preventivo',
  ORDINE_CLIENTE: 'Ordine cliente',
  ACCONTO_CLIENTE: 'Acconto cliente',
  DISTINTA: 'Distinta',
  SAL_DDT: 'SAL / DDT',
  FATTURA: 'Fattura',
  ORDINE_FORNITORE: 'Ordine fornitore',
  FATTURA_FORNITORE: 'Fattura fornitore',
};

const TIPI_VALIDI = Object.keys(LABEL_TIPO);

interface SedeInfo {
  id: number;
  nome: string;
  societaId: number;
  societaNome: string | null;
}

interface Condizione {
  tipoDocumento: string;
  testoHtml: string | null;
  updatedAt: string;
  updatedByName: string | null;
}

export default function CondizioniDocumentoPage() {
  const { sedeId: sedeIdParam, tipoDocumento } = useParams<{ sedeId: string; tipoDocumento: string }>();
  const navigate = useNavigate();
  const sedeId = sedeIdParam ? Number(sedeIdParam) : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sede, setSede] = useState<SedeInfo | null>(null);
  const [condizione, setCondizione] = useState<Condizione | null>(null);
  const [draftHtml, setDraftHtml] = useState<string>('');
  const [dirty, setDirty] = useState(false);

  const tipoValido = !!tipoDocumento && TIPI_VALIDI.includes(tipoDocumento);

  // ── Caricamento ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sedeId || !tipoValido) {
      setError('Parametri non validi');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // @api GET /api/sedi/:id/condizioni/:tipoDocumento -> { sede: SedeInfo, condizione: Condizione | null }
        const res = await apiFetch<{ sede: SedeInfo; condizione: Condizione | null }>(
          `/api/sedi/${sedeId}/condizioni/${tipoDocumento}`
        );
        if (cancelled) return;
        setSede(res.sede);
        setCondizione(res.condizione);
        setDraftHtml(res.condizione?.testoHtml ?? '');
        setDirty(false);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sedeId, tipoDocumento, tipoValido]);

  // Registrazione dirty-form globale
  useDirtyForm(dirty, `Condizioni ${LABEL_TIPO[tipoDocumento || ''] || ''}`.trim());

  // ── Salvataggio ───────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!sedeId || !tipoValido) return;
    setSaving(true);
    setError('');
    try {
      // @api PUT /api/sedi/:id/condizioni/:tipoDocumento {"testoHtml":"string"} -> { ok: true, condizione: Condizione }
      const res = await apiFetch<{ ok: true; condizione: Condizione }>(
        `/api/sedi/${sedeId}/condizioni/${tipoDocumento}`,
        { method: 'PUT', body: JSON.stringify({ testoHtml: draftHtml }) }
      );
      setCondizione(res.condizione);
      setDraftHtml(res.condizione.testoHtml ?? '');
      setDirty(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  }, [sedeId, tipoDocumento, tipoValido, draftHtml]);

  const annulla = () => {
    if (dirty && !window.confirm('Ci sono modifiche non salvate. Tornare indietro?')) return;
    navigate('/impostazioni/sedi');
  };

  const tipoLabel = useMemo(
    () => (tipoDocumento && LABEL_TIPO[tipoDocumento]) || tipoDocumento || '',
    [tipoDocumento]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="condizioni-doc-page">
          <div className="inline-loading">Caricamento…</div>
        </div>
      </Layout>
    );
  }

  if (!tipoValido || !sede) {
    return (
      <Layout>
        <div className="condizioni-doc-page">
          <div className="bop-inline-error">{error || 'Parametri non validi'}</div>
          <BopBackLink to="/impostazioni/sedi" label="Torna alle sedi" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="condizioni-doc-page">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <div className="condizioni-doc-breadcrumb bop-text-muted">
              Impostazioni › Sedi › <strong>{sede.nome}</strong>
              {sede.societaNome && <span className="bop-text-muted"> ({sede.societaNome})</span>}
            </div>
            <h1 className="bop-page-title">Condizioni generali — {tipoLabel}</h1>
            <p className="bop-text-muted">
              Questo testo verrà copiato automaticamente in coda ad ogni nuovo documento di tipo {tipoLabel.toLowerCase()} creato per questa sede.
            </p>
          </div>
          <div className="condizioni-doc-actions">
            <button className="bop-btn bop-btn-secondary" onClick={annulla} disabled={saving}>Annulla</button>
            <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        {condizione?.updatedAt && (
          <div className="condizioni-doc-meta bop-text-muted">
            Ultima modifica: {new Date(condizione.updatedAt).toLocaleString('it-IT')}
            {condizione.updatedByName && <> da <strong>{condizione.updatedByName}</strong></>}
          </div>
        )}

        <div className="bop-card">
          <CondizioniHtmlEditor
            value={draftHtml}
            onChange={(html) => { setDraftHtml(html); setDirty(true); }}
          />
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
